// components/invoice.js
import React, { useEffect, useState } from "react";
import "../screens/css/ProjectOverview.css";
import { Nav } from "./nav";
import { useAuth } from "../navigation/AuthProvider";
import axios from "axios";
import { BACKEND_URL } from "../constants";
import {
  FaFileInvoice,
  FaFilter,
  FaSearch,
  FaCalendarAlt,
  FaBuilding,
  FaProjectDiagram,
  FaListAlt,
  FaEye,
  FaTimes,
  FaInfoCircle,
  FaMoneyBillWave,
  FaFileContract,
} from "react-icons/fa";

const months = [
  "Januari", "Februari", "Mars", "April", "Maj", "Juni",
  "Juli", "Augusti", "September", "Oktober", "November", "December"
];

// Timesmart färger uppdaterade för att matcha ProjecGroups-stilen
const TS_COLORS = {
  primary: "#5caef6ff",
  lightBlue: "#EAF2FF",
  blue: "#2D6AE3",
  border: "#E5E7EB",
  textDark: "#1F2937",
  textMuted: "#6B7280",
  white: "#FFFFFF",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
  background: "#F9FAFB",
};

const Invoice = () => {
  const {
    projects,
    draftSchedule,
    company,
    user,
    customers,
  } = useAuth();

  const now = new Date();
  const defaultMonth = now.getMonth();
  const defaultYear = now.getFullYear();

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedProject, setSelectedProject] = useState("");

  const [allShifts, setAllShifts] = useState(draftSchedule || []);
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);

  // State för manuella timpriser - nu per kund+projekt kombination
  const [hourlyRates, setHourlyRates] = useState({});
  // State för beräknade totalbelopp per kund - startar alltid på 0
  const [calculatedAmounts, setCalculatedAmounts] = useState({});

  // Totalstatistik
  const [totalInfo, setTotalInfo] = useState({
    totalHours: 0,
    totalAmount: 0,
    totalProjects: 0,
    totalCustomers: 0,
  });

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  const [showInvoiceConfirmation, setShowInvoiceConfirmation] = useState(false);
  const [invoiceToCreate, setInvoiceToCreate] = useState(null);

  const currentYears = [selectedYear - 1, selectedYear, selectedYear + 1];

  /** ---------- Helper Functions ---------- */
  const isValidTime = (t) => typeof t === "string" && /^\d{2}:\d{2}$/.test(t);

  const parseTimeToMinutes = (t) => {
    if (!isValidTime(t)) return null;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const isShiftCompleteForReports = (s) => Boolean(
    s &&
    s.status === "complete" &&
    s.date &&
    isValidTime(s.startTime) &&
    isValidTime(s.endTime) &&
    s.project &&
    Array.isArray(s.employees) &&
    s.employees.some(e => !!e?.employeeId)
  );

  // Beräkna timmar för ett pass
  const calculateShiftHours = (shift) => {
    const start = parseTimeToMinutes(shift.startTime);
    const endRaw = parseTimeToMinutes(shift.endTime);
    
    if (start == null || endRaw == null) return 0;

    let end = endRaw <= start ? endRaw + 1440 : endRaw;
    let duration = (end - start) / 60;

    // Dra av raster (om de finns)
    const bs = parseTimeToMinutes(shift.breakStartTime);
    const beRaw = parseTimeToMinutes(shift.breakEndTime);
    if (bs != null && beRaw != null) {
      const be = beRaw <= bs ? beRaw + 1440 : beRaw;
      duration -= (be - bs) / 60;
    }

    return Math.max(0, duration);
  };

  // Beräkna totalt belopp för en faktura baserat på timmar och timpris
  const calculateInvoiceAmount = (shifts, hourlyRate = 0) => {
    let totalHours = 0;

    shifts.forEach(shift => {
      totalHours += calculateShiftHours(shift);
    });

    return {
      totalHours: parseFloat(totalHours.toFixed(2)),
      totalAmount: parseFloat((totalHours * hourlyRate).toFixed(2)),
      hourlyRate: hourlyRate
    };
  };

  // Hämta timpris för specifik kund+projekt kombination
  const getHourlyRate = (customerId, projectId) => {
    const key = `${customerId}_${projectId}`;
    return hourlyRates[key] !== undefined ? hourlyRates[key] : 0;
  };

  // Uppdatera timpris för specifik kund+projekt kombination
  const updateHourlyRate = (customerId, projectId, value) => {
    const key = `${customerId}_${projectId}`;
    const numericValue = parseFloat(value) || 0;
    
    setHourlyRates(prev => ({
      ...prev,
      [key]: numericValue
    }));
    
    // Beräkna om totalbeloppet för den kunden
    const invoice = invoiceData.find(inv => inv.customerId === customerId);
    if (!invoice) return;
    
    let totalAmount = 0;
    let totalHours = 0;
    
    invoice.projectDetails.forEach(project => {
      const projectShifts = invoice.allShifts.filter(s => s.project === project.projectId);
      const projectHourlyRate = getHourlyRate(customerId, project.projectId);
      const projectCalc = calculateInvoiceAmount(projectShifts, projectHourlyRate);
      totalAmount += projectCalc.totalAmount;
      totalHours += projectCalc.totalHours;
    });
    
    setCalculatedAmounts(prev => ({
      ...prev,
      [customerId]: parseFloat(totalAmount.toFixed(2))
    }));
    
    // Uppdatera även totalstatistik
    setTotalInfo(prev => {
      let newTotalAmount = 0;
      invoiceData.forEach(inv => {
        if (inv.customerId === customerId) {
          newTotalAmount += parseFloat(totalAmount.toFixed(2));
        } else {
          newTotalAmount += (calculatedAmounts[inv.customerId] || 0);
        }
      });
      
      return {
        ...prev,
        totalAmount: parseFloat(newTotalAmount.toFixed(2))
      };
    });
  };

  // Uppdatera alla projekt för en kund med samma timpris (från huvudlistan)
  const updateAllProjectsForCustomer = (customerId, value) => {
    const invoice = invoiceData.find(inv => inv.customerId === customerId);
    if (!invoice) return;
    
    let totalAmount = 0;
    let totalHours = 0;
    
    invoice.projectDetails.forEach(project => {
      updateHourlyRate(customerId, project.projectId, value);
      const projectShifts = invoice.allShifts.filter(s => s.project === project.projectId);
      const projectCalc = calculateInvoiceAmount(projectShifts, parseFloat(value) || 0);
      totalAmount += projectCalc.totalAmount;
      totalHours += projectCalc.totalHours;
    });
    
    setCalculatedAmounts(prev => ({
      ...prev,
      [customerId]: parseFloat(totalAmount.toFixed(2))
    }));
  };

  // Beräkna genomsnittligt timpris för en faktura
  const getAverageHourlyRate = (invoice) => {
    if (!invoice.projectDetails || invoice.projectDetails.length === 0) return 0;
    
    const total = invoice.projectDetails.reduce((sum, project) => {
      const rate = getHourlyRate(invoice.customerId, project.projectId);
      return sum + rate;
    }, 0);
    
    return total / invoice.projectDetails.length;
  };

  // Beräkna faktura-data - grupperat per kund
  const calculateInvoiceData = (shifts) => {
    const filtered = (shifts || []).filter(s => {
      if (!isShiftCompleteForReports(s)) return false;
      const [y, m] = String(s.date).split("-").map(Number);
      if (!(y === selectedYear && m === selectedMonth + 1)) return false;
      if (selectedCustomer && s.customer && String(s.customer) !== String(selectedCustomer)) return false;
      if (selectedProject && s.project !== selectedProject) return false;
      return true;
    });

    // Gruppera först per kund, sedan per projekt inom kunden
    const customersMap = {};
    
    filtered.forEach(shift => {
      const project = projects.find(p => p._id === shift.project);
      if (!project) return;
      
      const customerId = project.customer;
      const projectId = shift.project;
      
      if (!customersMap[customerId]) {
        customersMap[customerId] = {
          projects: {},
          totalHours: 0,
          totalAmount: 0,
          shiftCount: 0
        };
      }
      
      if (!customersMap[customerId].projects[projectId]) {
        customersMap[customerId].projects[projectId] = {
          shifts: [],
          totalHours: 0,
          totalAmount: 0,
          shiftCount: 0
        };
      }
      
      customersMap[customerId].projects[projectId].shifts.push(shift);
      customersMap[customerId].shiftCount += 1;
    });

    const results = [];
    let totalHoursAll = 0;
    let totalAmountAll = 0;
    const uniqueCustomers = new Set();

    Object.entries(customersMap).forEach(([customerId, customerData]) => {
      const customer = customers.find(c => c._id === customerId);
      if (!customer) return;
      
      uniqueCustomers.add(customerId);
      
      // Beräkna per projekt för denna kund
      let customerTotalHours = 0;
      const projectDetails = [];
      
      Object.entries(customerData.projects).forEach(([projectId, projectData]) => {
        const project = projects.find(p => p._id === projectId);
        if (!project) return;
        
        // Initiera timpris till 0 om det inte finns
        const key = `${customerId}_${projectId}`;
        if (hourlyRates[key] === undefined) {
          setHourlyRates(prev => ({
            ...prev,
            [key]: 0
          }));
        }
        
        // Initiera beräknat belopp till 0 om det inte finns
        if (calculatedAmounts[customerId] === undefined) {
          setCalculatedAmounts(prev => ({
            ...prev,
            [customerId]: 0
          }));
        }
        
        const currentRate = getHourlyRate(customerId, projectId);
        const invoiceCalc = calculateInvoiceAmount(projectData.shifts, currentRate);
        
        projectDetails.push({
          projectId,
          projectName: project.name || "Okänt projekt",
          totalHours: invoiceCalc.totalHours,
          totalAmount: invoiceCalc.totalAmount,
          hourlyRate: currentRate,
          shiftCount: projectData.shifts.length,
          shifts: projectData.shifts
        });
        
        customerTotalHours += invoiceCalc.totalHours;
      });
      
      results.push({
        customerId,
        customerName: customer.name || "Okänd kund",
        totalHours: parseFloat(customerTotalHours.toFixed(2)),
        totalAmount: calculatedAmounts[customerId] || 0,
        shiftCount: customerData.shiftCount,
        projectCount: projectDetails.length,
        projectDetails: projectDetails,
        startDate: filtered[0]?.date,
        endDate: filtered[filtered.length - 1]?.date,
        allShifts: Object.values(customerData.projects).flatMap(p => p.shifts),
        // Bevara ordningen baserat på när de lästes in
        orderIndex: results.length
      });

      totalHoursAll += customerTotalHours;
      totalAmountAll += (calculatedAmounts[customerId] || 0);
    });

    // BEVARAR ORDNINGEN - sortera INTE efter belopp
    // results.sort((a, b) => b.totalAmount - a.totalAmount);

    return {
      results,
      summary: {
        totalHours: parseFloat(totalHoursAll.toFixed(2)),
        totalAmount: parseFloat(totalAmountAll.toFixed(2)),
        totalProjects: Object.keys(customersMap).reduce((acc, customerId) => {
          return acc + Object.keys(customersMap[customerId].projects).length;
        }, 0),
        totalCustomers: uniqueCustomers.size,
      },
    };
  };

  const updateData = (shifts) => {
    const { results, summary } = calculateInvoiceData(shifts);
    setInvoiceData(results);
    setTotalInfo(summary);
  };

  // Uppdatera data när timpriser eller beräknade belopp ändras
  useEffect(() => {
    updateData(allShifts);
  }, [hourlyRates, calculatedAmounts]);

  /** ---------- Hämta data ---------- */
  useEffect(() => {
    const exists = (allShifts || []).some(s => {
      if (!isShiftCompleteForReports(s)) return false;
      const d = new Date(s.date);
      return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
    });

    if (exists) {
      updateData(allShifts);
      return;
    }

    const fetchShifts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/get-shifts-for-month`,
          {
            params: {
              userId: user._id,
              companyId: company._id,
              selectedMonth: selectedMonth + 1,
              selectedYear,
            },
          }
        );
        const newShifts = data?.shifts || [];
        const filteredOld = (allShifts || []).filter(s => {
          const d = new Date(s.date);
          return !(d.getMonth() === selectedMonth && d.getFullYear() === selectedYear);
        });
        const merged = [...filteredOld, ...newShifts];
        setAllShifts(merged);
        updateData(merged);
      } catch (err) {
        console.error("Failed to fetch shifts:", err);
      }
      setLoading(false);
    };

    fetchShifts();
  }, [selectedMonth, selectedYear, selectedCustomer, selectedProject]);

  /** ---------- Skapa faktura för kund ---------- */
  const handleCreateInvoice = (invoice) => {
    setInvoiceToCreate(invoice);
    setShowInvoiceConfirmation(true);
  };

  const confirmCreateInvoice = () => {
    if (!invoiceToCreate) return;
    
    // Här skulle du anropa ett API för att skapa fakturan i ditt system
    console.log("Skapar faktura för:", invoiceToCreate.customerName);
    console.log("Belopp:", calculatedAmounts[invoiceToCreate.customerId] || 0, "SEK");
    console.log("Period:", months[selectedMonth], selectedYear);
    
    // Visa bekräftelsemeddelande
    alert(`Faktura för ${invoiceToCreate.customerName} har skapats!\nBelopp: ${calculatedAmounts[invoiceToCreate.customerId] || 0} SEK\nPeriod: ${months[selectedMonth]} ${selectedYear}`);
    
    setShowInvoiceConfirmation(false);
    setInvoiceToCreate(null);
  };

  /** ---------- UI ---------- */
  return (
    <div className="projects-page" style={{ padding: 20, minHeight: "100vh", background: TS_COLORS.background }}>
      <Nav />

      {/* Header */}
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background:
                "linear-gradient(135deg, rgba(139,92,246,.2), rgba(59,130,246,.15))",
              display: "grid",
              placeItems: "center",
              border: `1px solid ${TS_COLORS.border}`,
            }}
          >
            <FaFileInvoice style={{ color: TS_COLORS.primary, opacity: 0.9 }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h1 style={{ fontSize: "1.6rem", color: TS_COLORS.textDark, margin: 0 }}>
              Fakturaöversikt
            </h1>
            <p style={{ margin: 0, marginTop: 4, fontSize: 12, color: TS_COLORS.textMuted }}>
              Skapa och hantera fakturor per kund baserat på registrerade arbetstimmar.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FaCalendarAlt style={{ color: TS_COLORS.textMuted, fontSize: 14 }} />
          <span style={{ fontSize: 13, color: TS_COLORS.textMuted }}>
            {months[selectedMonth]} {selectedYear}
          </span>
        </div>
      </div>

      {/* Filterbar */}
      <div
        style={{
          background: TS_COLORS.white,
          border: `1px solid ${TS_COLORS.border}`,
          borderRadius: 12,
          padding: 12,
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        {/* År och månad */}
        <div style={{ display: "flex", gap: 8 }}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={selectStyle}
          >
            {currentYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            style={selectStyle}
          >
            {months.map((month, i) => (
              <option key={month} value={i}>{month}</option>
            ))}
          </select>
        </div>

        {/* Kund-filter - FÖRKORTAT */}
        <div style={{ position: "relative", minWidth: "150px" }}>
          <FaBuilding
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: TS_COLORS.textMuted,
              fontSize: 13,
            }}
          />
          <select
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            style={{ 
              ...selectStyle, 
              paddingLeft: 30,
              width: "100%",
              maxWidth: "150px"
            }}
          >
            <option value="">Alla kunder</option>
            {(customers || []).map((customer) => (
              <option key={customer._id} value={customer._id}>
                {customer.name.length > 20 ? customer.name.substring(0, 20) + "..." : customer.name}
              </option>
            ))}
          </select>
        </div>

        {/* Projekt-filter - FÖRKORTAT */}
        <div style={{ position: "relative", minWidth: "150px" }}>
          <FaProjectDiagram
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: TS_COLORS.textMuted,
              fontSize: 13,
            }}
          />
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            style={{ 
              ...selectStyle, 
              paddingLeft: 30,
              width: "100%",
              maxWidth: "150px"
            }}
          >
            <option value="">Alla projekt</option>
            {(projects || []).map((project) => (
              <option key={project._id} value={project._id}>
                {project.name.length > 20 ? project.name.substring(0, 20) + "..." : project.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sök */}
        <div style={{ position: "relative", flex: 1, minWidth: "200px", maxWidth: "300px" }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: TS_COLORS.textMuted,
              fontSize: 13,
            }}
          />
          <input
            type="text"
            placeholder="Sök kund..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              height: 34,
              padding: "0 12px 0 30px",
              borderRadius: 8,
              border: `1px solid ${TS_COLORS.border}`,
              fontSize: 13,
              outline: "none",
              background: TS_COLORS.white,
            }}
          />
        </div>

        {/* Statistik */}
        <div
          style={{
            marginLeft: "auto",
            fontSize: 13,
            color: TS_COLORS.textMuted,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span>Kunder</span>
            <span style={countBadgeStyle}>{totalInfo.totalCustomers}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span>Timmar</span>
            <span style={countBadgeStyle}>{totalInfo.totalHours}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span>Belopp</span>
            <span style={countBadgeStyle}>
              {totalInfo.totalAmount.toFixed(0)} SEK
            </span>
          </div>
        </div>
      </div>

      {/* List headers */}
      <div
        className="column-headers"
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr 1fr 1fr 180px",
          padding: "10px 12px",
          background: TS_COLORS.white,
          border: `1px dashed ${TS_COLORS.border}`,
          borderRadius: 8,
          color: "#374151",
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        <p style={{ margin: 0 }}>Kund</p>
        <p style={{ margin: 0 }}>Timmar</p>
        <p style={{ margin: 0 }}>Timpris (SEK)</p>
        <p style={{ margin: 0 }}>Belopp (SEK)</p>
        <div style={{ textAlign: "right" }}>Åtgärd</div>
      </div>

      {/* Fakturalista */}
      <div className="groups-list">
        {loading ? (
          <div
            style={{
              padding: 40,
              color: TS_COLORS.textMuted,
              fontSize: 14,
              background: TS_COLORS.white,
              border: `1px solid ${TS_COLORS.border}`,
              borderRadius: 10,
              textAlign: "center",
            }}
          >
            Laddar faktureringsdata...
          </div>
        ) : invoiceData.length === 0 ? (
          <div
            style={{
              padding: 40,
              color: TS_COLORS.textMuted,
              fontSize: 14,
              background: TS_COLORS.white,
              border: `1px solid ${TS_COLORS.border}`,
              borderRadius: 10,
              textAlign: "center",
            }}
          >
            {searchQuery || selectedCustomer || selectedProject
              ? "Inga fakturor matchar dina filterkriterier."
              : "Inga faktureringsdata tillgänglig för den valda perioden."}
          </div>
        ) : (
          invoiceData
            .filter(inv => 
              inv.customerName.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map(invoice => {
              const avgHourlyRate = getAverageHourlyRate(invoice);
              const calculatedAmount = calculatedAmounts[invoice.customerId] || 0;
              
              return (
                <div
                  key={invoice.customerId}
                  className="group-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 1fr 1fr 180px",
                    alignItems: "center",
                    gap: 8,
                    padding: "14px 12px",
                    background: TS_COLORS.white,
                    borderRadius: 8,
                    borderLeft: `4px solid ${TS_COLORS.primary}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,.06)",
                    position: "relative",
                    marginTop: 6,
                  }}
                >
                  {/* Kund */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: "rgba(92,174,246,.15)",
                        display: "grid",
                        placeItems: "center",
                        color: TS_COLORS.primary,
                      }}
                    >
                      <FaBuilding style={{ fontSize: 14 }} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        style={{ fontSize: 14, color: TS_COLORS.textDark, fontWeight: 600 }}
                      >
                        {invoice.customerName}
                      </span>
                      <span style={{ fontSize: 12, color: TS_COLORS.textMuted }}>
                        {invoice.shiftCount} pass • {invoice.projectCount} projekt
                      </span>
                    </div>
                  </div>

                  {/* Timmar */}
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: TS_COLORS.textDark }}>
                      {invoice.totalHours.toFixed(2)} h
                    </span>
                  </div>

                  {/* Timpris - Input där man kan skriva själv */}
                  <div>
                    <div style={{ position: "relative", width: "100%" }}>
                      <input
                        type="number"
                        value={Math.round(avgHourlyRate)}
                        onChange={(e) => {
                          // Uppdatera alla projekt för denna kund med samma timpris
                          updateAllProjectsForCustomer(invoice.customerId, e.target.value);
                        }}
                        style={{
                          width: "100%",
                          height: 34,
                          padding: "0 8px",
                          borderRadius: 6,
                          border: `1px solid ${TS_COLORS.border}`,
                          fontSize: 13,
                          outline: "none",
                          background: TS_COLORS.white,
                          fontWeight: 600,
                          color: TS_COLORS.textDark,
                          textAlign: "center",
                        }}
                        min="0"
                        step="10"
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 11,
                          color: TS_COLORS.textMuted,
                        }}
                      >
                        SEK/h
                      </span>
                    </div>
                  </div>

                  {/* Belopp - UPPDATERAS AUTOMATISKT */}
                  <div>
                    <span style={{ 
                      fontSize: 14, 
                      fontWeight: 700, 
                      color: calculatedAmount > 0 ? TS_COLORS.success : TS_COLORS.textMuted,
                    }}>
                      {calculatedAmount.toFixed(0)} SEK
                    </span>
                  </div>

                  {/* Åtgärder */}
                  <div style={{ justifySelf: "end", display: "flex", gap: 8 }}>
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowInvoicePopup(true);
                      }}
                      style={{
                        background: TS_COLORS.white,
                        border: `1px solid ${TS_COLORS.border}`,
                        cursor: "pointer",
                        color: TS_COLORS.textMuted,
                        padding: "6px 10px",
                        borderRadius: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                      title="Visa detaljer"
                    >
                      <FaEye />
                      Visa
                    </button>
                    <button
                      onClick={() => handleCreateInvoice(invoice)}
                      style={{
                        background: calculatedAmount > 0 ? TS_COLORS.success : "#CBD5E1",
                        border: "none",
                        cursor: calculatedAmount > 0 ? "pointer" : "not-allowed",
                        color: TS_COLORS.white,
                        padding: "6px 10px",
                        borderRadius: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        boxShadow: calculatedAmount > 0 ? "0 2px 8px rgba(16, 185, 129, 0.25)" : "none",
                      }}
                      title={calculatedAmount > 0 ? "Skapa faktura" : "Ange timpris för att skapa faktura"}
                      disabled={calculatedAmount === 0}
                    >
                      <FaFileContract />
                      Faktura
                    </button>
                  </div>
                </div>
              );
            })
        )}
      </div>

      {/* Popup för fakturadetaljer */}
      {showInvoicePopup && selectedInvoice && (
        <div style={overlayStyle} onClick={() => setShowInvoicePopup(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(92,174,246,.15)",
                    color: TS_COLORS.primary,
                  }}
                >
                  <FaFileInvoice />
                </div>
                <h4 style={{ margin: 0, color: TS_COLORS.textDark }}>
                  Fakturadetaljer • {selectedInvoice.customerName}
                </h4>
              </div>
              <button
                onClick={() => setShowInvoicePopup(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: TS_COLORS.textMuted,
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Sammanfattning */}
            <div style={summaryRowStyle}>
              <div style={summaryChipStyle}>
                <span style={chipLabelStyle}>Antal projekt</span>
                <span style={chipValueStyle}>{selectedInvoice.projectCount}</span>
              </div>
              <div style={summaryChipStyle}>
                <span style={chipLabelStyle}>Antal pass</span>
                <span style={chipValueStyle}>{selectedInvoice.shiftCount}</span>
              </div>
              <div style={summaryChipStyle}>
                <span style={chipLabelStyle}>Totala timmar</span>
                <span style={chipValueStyle}>{selectedInvoice.totalHours.toFixed(2)}</span>
              </div>
              <div style={summaryChipStyle}>
                <span style={chipLabelStyle}>Genomsnittligt timpris</span>
                <span style={chipValueStyle}>
                  {getAverageHourlyRate(selectedInvoice).toFixed(0)} SEK/h
                </span>
              </div>
            </div>

            {/* Totalbelopp - beräknat automatiskt */}
            <div style={{ marginBottom: 16 }}>
              <div style={badgeRowStyle}>
                <span style={blueBadgeStyle}>Totalbelopp för faktura</span>
                <span style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: calculatedAmounts[selectedInvoice.customerId] > 0 ? TS_COLORS.success : TS_COLORS.textMuted,
                }}>
                  {(calculatedAmounts[selectedInvoice.customerId] || 0).toFixed(0)} SEK
                </span>
              </div>
              <div style={{ fontSize: 12, color: TS_COLORS.textMuted, marginTop: 4 }}>
                {calculatedAmounts[selectedInvoice.customerId] > 0 
                  ? `Beräknat baserat på ${selectedInvoice.totalHours.toFixed(2)} timmar × ${getAverageHourlyRate(selectedInvoice).toFixed(0)} SEK/h`
                  : "Ange timpris för att beräkna belopp"}
              </div>
            </div>

            {/* Projektlista */}
            <h5 style={sectionTitleStyle}>Projekt för denna kund</h5>
            <div style={shiftListStyle}>
              {selectedInvoice.projectDetails.map((project, idx) => {
                const projectHourlyRate = getHourlyRate(selectedInvoice.customerId, project.projectId);
                const projectCalc = calculateInvoiceAmount(project.shifts, projectHourlyRate);
                
                return (
                  <div key={idx} style={shiftCardStyle}>
                    <div style={shiftTopRowStyle}>
                      <div style={{ flex: 1 }}>
                        <div style={badgeRowStyle}>
                          <span style={blueBadgeStyle}>Projekt</span>
                          <span style={badgeValueStyle}>{project.projectName}</span>
                        </div>
                        <div style={badgeRowStyle}>
                          <span style={blueBadgeStyle}>Timpris</span>
                          <span style={badgeValueStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <input
                                type="number"
                                value={projectHourlyRate}
                                onChange={(e) => updateHourlyRate(selectedInvoice.customerId, project.projectId, e.target.value)}
                                style={{
                                  width: '80px',
                                  padding: '4px',
                                  border: `1px solid ${TS_COLORS.border}`,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontSize: '14px',
                                  background: TS_COLORS.white,
                                }}
                                min="0"
                                step="10"
                              />
                              <span style={{ fontSize: '12px' }}>SEK/h</span>
                            </div>
                          </span>
                        </div>
                      </div>
                      <div>
                        <div style={badgeRowStyle}>
                          <span style={blueBadgeStyle}>Timmar</span>
                          <span style={badgeValueStyle}>{projectCalc.totalHours.toFixed(2)} h</span>
                        </div>
                        <div style={badgeRowStyle}>
                          <span style={blueBadgeStyle}>Belopp</span>
                          <span style={{ 
                            ...badgeValueStyle, 
                            color: projectCalc.totalAmount > 0 ? TS_COLORS.success : TS_COLORS.textMuted 
                          }}>
                            {projectCalc.totalAmount.toFixed(2)} SEK
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 8, color: TS_COLORS.textMuted, fontSize: "12px" }}>
                      Antal pass: {project.shiftCount} st
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Åtgärdsknappar */}
            <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center" }}>
              <button
                onClick={() => handleCreateInvoice(selectedInvoice)}
                style={{
                  ...primaryBtnStyle,
                  background: calculatedAmounts[selectedInvoice.customerId] > 0 ? TS_COLORS.success : "#CBD5E1",
                  cursor: calculatedAmounts[selectedInvoice.customerId] > 0 ? "pointer" : "not-allowed"
                }}
                title={calculatedAmounts[selectedInvoice.customerId] > 0 ? "Skapa faktura" : "Ange timpris för att skapa faktura"}
                disabled={calculatedAmounts[selectedInvoice.customerId] === 0}
              >
                <FaFileContract style={{ marginRight: 6 }} />
                Skapa faktura
              </button>
              <button
                onClick={() => setShowInvoicePopup(false)}
                style={ghostBtnStyle}
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bekräftelsedialog för fakturaskapande */}
      {showInvoiceConfirmation && invoiceToCreate && (
        <div style={overlayStyle} onClick={() => setShowInvoiceConfirmation(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(16, 185, 129, 0.15)",
                    color: TS_COLORS.success,
                  }}
                >
                  <FaFileContract />
                </div>
                <h4 style={{ margin: 0, color: TS_COLORS.textDark }}>
                  Skapa faktura • {invoiceToCreate.customerName}
                </h4>
              </div>
              <button
                onClick={() => setShowInvoiceConfirmation(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: TS_COLORS.textMuted,
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ marginBottom: 24 }}>
              <p style={{ color: TS_COLORS.textDark, marginBottom: 16 }}>
                Är du säker på att du vill skapa en faktura för <strong>{invoiceToCreate.customerName}</strong>?
              </p>
              
              <div style={{ backgroundColor: TS_COLORS.white, padding: 16, borderRadius: 8, border: `1px solid ${TS_COLORS.border}` }}>
                <div style={badgeRowStyle}>
                  <span style={blueBadgeStyle}>Period</span>
                  <span style={badgeValueStyle}>{months[selectedMonth]} {selectedYear}</span>
                </div>
                <div style={badgeRowStyle}>
                  <span style={blueBadgeStyle}>Totalbelopp</span>
                  <span style={badgeValueStyle}>
                    {(calculatedAmounts[invoiceToCreate.customerId] || 0).toFixed(0)} SEK
                  </span>
                </div>
                <div style={badgeRowStyle}>
                  <span style={blueBadgeStyle}>Antal projekt</span>
                  <span style={badgeValueStyle}>{invoiceToCreate.projectCount}</span>
                </div>
                <div style={badgeRowStyle}>
                  <span style={blueBadgeStyle}>Arbetade timmar</span>
                  <span style={badgeValueStyle}>{invoiceToCreate.totalHours.toFixed(2)} h</span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowInvoiceConfirmation(false)}
                style={ghostBtnStyle}
              >
                Avbryt
              </button>
              <button
                onClick={confirmCreateInvoice}
                style={primaryBtnStyle}
              >
                Ja, skapa faktura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info-meddelande */}
      {invoiceData.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '12px 16px',
          backgroundColor: TS_COLORS.lightBlue,
          border: `1px solid ${TS_COLORS.border}`,
          borderRadius: 10,
          display: 'flex',
          alignItems: "center",
          gap: 10,
          color: TS_COLORS.textDark
        }}>
          <FaInfoCircle style={{ color: TS_COLORS.primary, fontSize: 16 }} />
          <div style={{ fontSize: 13 }}>
            <strong>Tips:</strong> Alla fakturor startar med 0 kr. Ange timpris för att beräkna belopp.
            Ordningen på kunderna bevaras och sorteras inte automatiskt.
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Stilar ----------
const countBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 8px",
  height: 22,
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background: "#F3F4F6",
  color: "#374151",
};

const selectStyle = {
  height: 34,
  padding: "0 12px",
  borderRadius: 8,
  border: `1px solid ${TS_COLORS.border}`,
  fontSize: 13,
  outline: "none",
  background: TS_COLORS.white,
  minWidth: 120,
  cursor: "pointer",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.5)",
  display: "grid",
  placeItems: "center",
  zIndex: 1000,
};

const modalStyle = {
  width: "min(800px, 92vw)",
  background: "#F9FAFB",
  border: "1px solid #E5E7EB",
  borderRadius: 16,
  padding: 20,
  boxShadow: "0 12px 32px rgba(0,0,0,.25)",
  maxHeight: "82vh",
  overflowY: "auto",
};

const summaryRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 10,
  marginBottom: 16,
};

const summaryChipStyle = {
  backgroundColor: TS_COLORS.white,
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: 10,
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  textAlign: "center",
  minHeight: 72,
};

const chipLabelStyle = {
  fontSize: 12,
  color: TS_COLORS.textMuted,
  fontWeight: 600,
  letterSpacing: 0.3,
};

const chipValueStyle = {
  fontSize: 18,
  fontWeight: 800,
  color: TS_COLORS.textDark,
  lineHeight: 1,
};

const labelStyle = { 
  fontSize: 13, 
  color: "#374151", 
  display: "block", 
  marginBottom: 6,
  fontWeight: 600 
};

const sectionTitleStyle = {
  marginTop: 16,
  marginBottom: 8,
  fontSize: 16,
  fontWeight: 600,
  color: TS_COLORS.textDark,
};

const shiftListStyle = {
  maxHeight: "40vh",
  overflowY: "auto",
  paddingRight: 8,
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: 10,
  padding: 12,
  backgroundColor: TS_COLORS.white,
};

const shiftCardStyle = {
  border: `1px solid ${TS_COLORS.border}`,
  background: TS_COLORS.white,
  borderRadius: 10,
  padding: 12,
  marginBottom: 12,
};

const shiftTopRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const badgeRowStyle = { 
  display: "flex", 
  alignItems: "center", 
  gap: 8, 
  marginBottom: 6 
};

const blueBadgeStyle = {
  background: TS_COLORS.lightBlue,
  border: `1px solid ${TS_COLORS.border}`,
  color: TS_COLORS.textDark,
  padding: "2px 8px",
  fontSize: 12,
  borderRadius: 999,
  fontWeight: 600,
};

const badgeValueStyle = { 
  color: TS_COLORS.textDark, 
  fontWeight: 600,
  fontSize: 13,
};

const primaryBtnStyle = {
  background: TS_COLORS.success,
  color: TS_COLORS.white,
  border: "none",
  borderRadius: 10,
  padding: "10px 16px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 6px 14px rgba(16, 185, 129, 0.25)",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const ghostBtnStyle = {
  background: TS_COLORS.white,
  color: TS_COLORS.textDark,
  border: `1px solid ${TS_COLORS.border}`,
  borderRadius: 10,
  padding: "10px 16px",
  fontWeight: 600,
  cursor: "pointer",
};

export default Invoice;
